import React from "react";
import Link from "next/link";
import InfiniteScroll from "react-infinite-scroll-component";
import NewNewsCard from "./new-news-card";
import {
  NewsListCardSummaryFragment,
  NewsSummaryFragment,
  VideoSummaryFragment,
} from "@graphql/generated/graphql";
import { Trans } from "@lingui/macro";
import VideoCard from "@components/videos/video-card";
import ShortsCard from "@components/shorts/shorts-card";
import classNames from "@lib/classNames";

export interface ICustomNewsListItemProps {
  index: number;
  child: JSX.Element;
}

interface IBaseNewsListProps {
  showPublisher?: boolean;
  hideGoodNewsLabel?: boolean;
  customNewsListItem?: ICustomNewsListItemProps;
}

interface ICompactNewsListProps extends IBaseNewsListProps {
  variant: "compact";
  news: NewsListCardSummaryFragment[];
}

interface IResponsiveNewsListProps extends IBaseNewsListProps {
  variant?: "responsive";
  news: (NewsSummaryFragment | NewsListCardSummaryFragment)[];
}

interface IWideNewsListProps extends IBaseNewsListProps {
  variant: "wide";
  news: NewsSummaryFragment[];
}

type INewsListProps =
  | ICompactNewsListProps
  | IResponsiveNewsListProps
  | IWideNewsListProps;

interface IVideosListProps {
  videos: VideoSummaryFragment[];
  showPublisher?: boolean;
}

interface IShortsGridProps {
  shorts: VideoSummaryFragment[];
}

export const NewsList: React.FunctionComponent<INewsListProps> = ({
  news,
  variant = "responsive",
  showPublisher = true,
  hideGoodNewsLabel = false,
  customNewsListItem,
}) => {
  if (variant === "wide") {
    return (
      <div className="flex flex-col items-stretch divide-y divide-light-background">
        {news.map((item, index) => (
          <div
            className={classNames(
              customNewsListItem && customNewsListItem.index + 1 == index
                ? "border-none py-4"
                : "py-4"
            )}
            key={item.id}
          >
            <NewNewsCard
              news={item}
              showPublisher={showPublisher}
              big={true}
              hideGoodNewsLabel={hideGoodNewsLabel}
            />
            {customNewsListItem &&
              customNewsListItem.index == index &&
              customNewsListItem.child}
          </div>
        ))}
      </div>
    );
  }

  const cardBig = variant === "compact" ? false : undefined;

  return (
    <div className="flex flex-col items-stretch divide-y divide-light-background">
      {news.map((item, index) => (
        <div
          className={classNames(
            // Overrides to remove the divider at the end of the card
            customNewsListItem && customNewsListItem.index + 1 == index
              ? "border-none py-4"
              : "py-4"
          )}
          key={item.id}
        >
          <NewNewsCard
            news={item}
            showPublisher={showPublisher}
            big={cardBig}
            hideGoodNewsLabel={hideGoodNewsLabel}
          />
          {customNewsListItem &&
            customNewsListItem.index == index &&
            customNewsListItem.child}
        </div>
      ))}
    </div>
  );
};

export const VideosList: React.FC<IVideosListProps> = ({
  videos,
  showPublisher = true,
}) => {
  return (
    <div className="flex flex-col items-stretch divide-y divide-light-background">
      {videos.map((video) => (
        <div className="py-4" key={video.id}>
          <VideoCard video={video} showPublisher={showPublisher} />
        </div>
      ))}
    </div>
  );
};

export const ShortsGrid: React.FC<IShortsGridProps> = ({ shorts }) => {
  return (
    <div className="grid grid-cols-2 gap-2 p-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5">
      {shorts.map((short) => (
        <ShortsCard short={short} key={short.id} />
      ))}
    </div>
  );
};

export interface InfiniteListProps {
  infiniteLoadingOption?: {
    className?: string;
    dataLength: number;
    hasMore?: boolean;
    scrollThreshold?: number;
    height?: number;
    next: () => any;
    loader: JSX.Element;
    endMessage?: JSX.Element;
  };
}

export const InfiniteList: React.FunctionComponent<InfiniteListProps> = ({
  infiniteLoadingOption,
  children,
}) => {
  return (
    <InfiniteScroll
      style={{ overflowX: "hidden" }}
      className={infiniteLoadingOption.className}
      dataLength={infiniteLoadingOption.dataLength}
      hasMore={infiniteLoadingOption.hasMore ?? true}
      loader={infiniteLoadingOption.loader}
      scrollThreshold={infiniteLoadingOption.scrollThreshold}
      height={infiniteLoadingOption.height}
      next={infiniteLoadingOption.next}
      endMessage={
        infiniteLoadingOption.endMessage ?? (
          <p className="py-4 text-center font-sen text-text-lightest">
            <Trans>End of list</Trans>
          </p>
        )
      }
    >
      {children}
    </InfiniteScroll>
  );
};
